import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Visibility, Logout, FileDownload, AccountBalanceWallet, School, InsertDriveFile } from '@mui/icons-material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import api from '../../services/api';

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [statusToUpdate, setStatusToUpdate] = useState('');

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        setLoading(true);
        const response = await api.getRegistrations();
        setRegistrations(response.data.registrations);
      } catch (error) {
        console.error('Error fetching registrations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExport = async () => {
    try {
      const response = await api.exportRegistrations();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'registrations.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting registrations:', error);
    }
  };

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      await api.updateRegistrationStatus(registrationId, newStatus);
      setRegistrations((prev) =>
        prev.map((registration) =>
          registration._id === registrationId ? { ...registration, status: newStatus } : registration
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDownloadFile = (fileUrl, fileType) => {
    try {
      // Construire l'URL complète si nécessaire
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3002';
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseUrl}${fileUrl}`;
      
      // Ouvrir le fichier dans un nouvel onglet (plus fiable que le téléchargement direct)
      window.open(fullUrl, '_blank');
      
      console.log(`Ouverture du fichier ${fileType}:`, fullUrl);
    } catch (error) {
      console.error(`Erreur lors de l'ouverture du fichier ${fileType}:`, error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Tableau de bord de {user?.username}
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Inscriptions</Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<FileDownload />}
                  onClick={handleExport}
                >
                  Exporter Excel
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Téléphone</TableCell>
                        <TableCell>N° CIN</TableCell>
                        <TableCell>Localité</TableCell>
                        <TableCell>Niveau Scolaire</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Pièces jointes</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {registrations.length > 0 ? (
                        registrations.map((registration) => (
                          <TableRow key={registration._id}>
                            <TableCell>{registration.firstName} {registration.lastName}</TableCell>
                            <TableCell>{registration.email}</TableCell>
                            <TableCell>{registration.phone}</TableCell>
                            <TableCell>{registration.cinNumber}</TableCell>
                            <TableCell>{registration.locality}</TableCell>
                            <TableCell>{registration.education}</TableCell>
                            <TableCell>
                              <Select
                                value={registration.status}
                                onChange={(e) => handleStatusChange(registration._id, e.target.value)}
                              >
                                <MenuItem value="pending">En attente</MenuItem>
                                <MenuItem value="approved">Approuvé</MenuItem>
                                <MenuItem value="rejected">Rejeté</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {registration.cinFile && (
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    title="Télécharger CIN"
                                    onClick={() => handleDownloadFile(registration.cinFile, 'CIN')}
                                  >
                                    <BadgeIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {registration.ribFile && (
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    title="Télécharger RIB"
                                    onClick={() => handleDownloadFile(registration.ribFile, 'RIB')}
                                  >
                                    <AccountBalanceWallet fontSize="small" />
                                  </IconButton>
                                )}
                                {registration.certificateFile && (
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    title="Télécharger Certificat"
                                    onClick={() => handleDownloadFile(registration.certificateFile, 'Certificat')}
                                  >
                                    <School fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton color="primary" onClick={() => setSelectedRegistration(registration)}>
                                <Visibility />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Aucune inscription trouvée
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Dialog open={!!selectedRegistration} onClose={() => setSelectedRegistration(null)} maxWidth="md" fullWidth>
          {selectedRegistration && (
             <>
             <DialogTitle>
               Détails de l'inscription de {selectedRegistration.firstName} {selectedRegistration.lastName}
             </DialogTitle>
             <DialogContent>
               <Grid container spacing={2}>
                 <Grid item xs={12} md={6}>
                   <Typography variant="subtitle1" fontWeight="bold">Email:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.email}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Téléphone:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.phone}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">N° CIN:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.cinNumber}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Date de naissance:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.birthDate}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Comment a-t-il entendu parler de nous:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.hearAboutUs || 'Non spécifié'}</Typography>
                 </Grid>
                 <Grid item xs={12} md={6}>
                   <Typography variant="subtitle1" fontWeight="bold">Adresse:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.address}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Localité:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.locality}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Niveau Scolaire:</Typography>
                   <Typography variant="body1" gutterBottom>{selectedRegistration.education}</Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Date d'inscription:</Typography>
                   <Typography variant="body1" gutterBottom>
                     {new Date(selectedRegistration.createdAt).toLocaleDateString()}
                   </Typography>
                   
                   <Typography variant="subtitle1" fontWeight="bold">Conditions acceptées:</Typography>
                   <Typography variant="body1" gutterBottom>
                     {selectedRegistration.termsAccepted ? 'Oui' : 'Non'}
                   </Typography>
                 </Grid>
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" fontWeight="bold">Motivation:</Typography>
                   <Typography variant="body1" paragraph>{selectedRegistration.motivation}</Typography>
                 </Grid>
                 
                 {/* Pièces jointes */}
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" fontWeight="bold">Pièces jointes:</Typography>
                   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                     {selectedRegistration.cinFile && (
                       <Button
                         variant="outlined"
                         startIcon={<BadgeIcon />}
                         onClick={() => handleDownloadFile(selectedRegistration.cinFile, 'CIN')}
                       >
                         CIN
                       </Button>
                     )}
                     {selectedRegistration.ribFile && (
                       <Button
                         variant="outlined"
                         startIcon={<AccountBalanceWallet />}
                         onClick={() => handleDownloadFile(selectedRegistration.ribFile, 'RIB')}
                       >
                         RIB
                       </Button>
                     )}
                     {selectedRegistration.certificateFile && (
                       <Button
                         variant="outlined"
                         startIcon={<School />}
                         onClick={() => handleDownloadFile(selectedRegistration.certificateFile, 'Certificat')}
                       >
                         Certificat Scolaire
                       </Button>
                     )}
                     {!selectedRegistration.cinFile && !selectedRegistration.ribFile && !selectedRegistration.certificateFile && (
                       <Typography variant="body2" color="text.secondary">Aucune pièce jointe</Typography>
                     )}
                   </Box>
                 </Grid>
               </Grid>
             </DialogContent>
             <DialogActions>
               <Button onClick={() => setSelectedRegistration(null)}>Fermer</Button>
             </DialogActions>
           </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export { UserDashboard };
